import { useState } from "react";
import { ChevronDown, ChevronRight, FileText, Save, Trash2, X } from "lucide-react";
import { MemoryEntryCard } from "./MemoryEntryCard";
import type { MemorySection, MemoryEntry } from "../../types/orgMemory";

type MemorySectionPanelProps = {
  section: MemorySection;
  categories: string[];
  isSaving: boolean;
  onUpdateSection: (updates: Partial<Pick<MemorySection, "category" | "tags">>) => void;
  onSaveEntry: (entryId: string, updates: Partial<Pick<MemoryEntry, "answer">>) => void;
  onDeleteEntry: (entryId: string) => void;
  onDeleteSection: () => void;
  onSave: () => void;
};

export const MemorySectionPanel = ({
  section,
  categories,
  isSaving,
  onUpdateSection,
  onSaveEntry,
  onDeleteEntry,
  onDeleteSection,
  onSave,
}: MemorySectionPanelProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [tagInput, setTagInput] = useState("");

  // Ensure the section's current category is selectable even if it is not (or
  // no longer) an active qhse_categories entry.
  const categoryOptions = section.category && !categories.includes(section.category)
    ? [section.category, ...categories]
    : categories;

  const addTag = () => {
    const tag = tagInput.trim();
    if (!tag || section.tags.includes(tag)) { setTagInput(""); return; }
    onUpdateSection({ tags: [...section.tags, tag] });
    setTagInput("");
  };

  const removeTag = (tag: string) =>
    onUpdateSection({ tags: section.tags.filter((t) => t !== tag) });

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3">
        <button
          className="flex flex-1 items-center gap-2 text-left"
          onClick={() => setIsOpen((o) => !o)}
        >
          {isOpen ? <ChevronDown className="size-4 shrink-0 text-gray-500" /> : <ChevronRight className="size-4 shrink-0 text-gray-500" />}
          <span className="font-semibold text-gray-900 truncate">{section.category || "Untitled"}</span>
          {section.documentTitle && (
            <span className="text-xs text-gray-500 truncate hidden sm:block">— {section.documentTitle}</span>
          )}
          <span className="ml-auto text-xs text-gray-400 shrink-0">
            {section.entries.length} {section.entries.length === 1 ? "entry" : "entries"}
          </span>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDeleteSection(); }}
          title="Delete section"
          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors shrink-0"
        >
          <Trash2 className="size-4" />
        </button>
      </div>

      {isOpen && (
        <div className="border-t border-gray-200 p-4 space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 rounded-lg border border-gray-200 bg-white p-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">Category</label>
              <select
                value={section.category}
                onChange={(e) => onUpdateSection({ category: e.target.value })}
                className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-sm focus:border-emerald-500 focus:outline-none"
              >
                <option value="" disabled>Select category</option>
                {categoryOptions.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">Document Title</label>
              <div className="flex items-center gap-1.5 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-sm text-gray-600">
                <FileText className="size-3.5 shrink-0 text-gray-400" />
                <span className="truncate">{section.documentTitle || "—"}</span>
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">Tags</label>
              <div className="flex flex-wrap gap-1 mb-2">
                {section.tags.map((tag) => (
                  <span key={tag} className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                    {tag}
                    <button onClick={() => removeTag(tag)} className="hover:text-emerald-900">
                      <X className="size-2.5" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                  className="flex-1 rounded border border-gray-200 px-2 py-1 text-sm focus:border-emerald-500 focus:outline-none"
                  placeholder="Add tag and press Enter"
                />
                <button onClick={addTag} className="rounded bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-700">Add</button>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {section.entries.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-400 italic">No entries. Delete this section or add entries via Boost Policy Q&A.</p>
            ) : (
              section.entries.map((entry) => (
                <MemoryEntryCard
                  key={entry.id}
                  entry={entry}
                  isSaving={isSaving}
                  onSaveAnswer={(answer) => onSaveEntry(entry.id, { answer })}
                  onDelete={() => onDeleteEntry(entry.id)}
                />
              ))
            )}
          </div>

          <div className="flex justify-end">
            <button
              onClick={onSave}
              disabled={isSaving}
              className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <Save className="size-4" />
              {isSaving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
